/**
 * Audit log viewer — /settings/custom-modules/$key/audit
 *
 * Shows a paginated read-only table of security-sensitive actions
 * taken against this module. Pulls from the custom.audit_log via
 * the modules.custom.audit.list procedure, filtered by moduleKey
 * from the URL so each module's audit tab shows only its own events.
 */

import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/custom-modules/$key/audit')({
  component: AuditLogPage,
})

function AuditLogPage() {
  const { key: moduleKey } = Route.useParams()
  const [page, setPage] = useState(1)
  const pageSize = 50

  const { data, isLoading } = useQuery(
    orpc.modules.custom.audit.list.queryOptions({
      input: { page, pageSize, moduleKey },
    }),
  )

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>Audit log</CardTitle>
          <p className="text-sm text-muted-foreground">
            Security-sensitive actions for this module.
          </p>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <Skeleton className="h-64 w-full" />
          ) : !data || data.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-8">
              No audit entries yet.
            </p>
          ) : (
            <>
              <div className="rounded-md border">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>When</TableHead>
                      <TableHead>Action</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Result</TableHead>
                      <TableHead>Details</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {data.entries.map((entry) => (
                      <TableRow key={entry.id}>
                        <TableCell className="text-xs text-muted-foreground whitespace-nowrap">
                          {new Date(entry.createdAt).toLocaleString()}
                        </TableCell>
                        <TableCell className="font-mono text-xs">{entry.action}</TableCell>
                        <TableCell className="font-mono text-xs">
                          {entry.userId ? entry.userId.slice(0, 8) : '—'}
                        </TableCell>
                        <TableCell>
                          <Badge variant={entry.allowed ? 'default' : 'destructive'}>
                            {entry.allowed ? 'Allowed' : 'Denied'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-xs text-muted-foreground max-w-md truncate">
                          {entry.metadata
                            ? JSON.stringify(entry.metadata)
                            : '—'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {data.pagination.totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-muted-foreground">
                    Page {data.pagination.page} of {data.pagination.totalPages} (
                    {data.pagination.total} entries)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage(page - 1)}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= data.pagination.totalPages}
                      onClick={() => setPage(page + 1)}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
